/**
 * Table row for a pair.
 */
import * as React from 'react';
import { User, GraduationCap, Calendar, Building2, BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SessionStudentTutorPair } from '@/types/inperson/pair.types';
import { deriveDisplay, formatPairSubjectsTooltip, type PageActions } from './pair-table-utils';

/** Props for {@link PairRow}. */
interface PairRowProps {
  pair: SessionStudentTutorPair;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: PageActions;
}

/** In-person pair table row (type, session, establishment, subjects). */
export const PairRow: React.FC<PairRowProps> = ({ pair, onView, onDelete, pageActions }) => {
  const canView = pageActions?.canViewRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? false;
  const {
    typeConfig,
    TypeIcon,
    sessionDate,
    sessionTimeRange,
    establishmentName,
    studentName,
    tutorName,
    tutorRoleLabel,
    studentLevel,
    subjects,
    subjectCountLabel,
  } = deriveDisplay(pair);

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{studentName}</span>
            {studentLevel && <span className="text-xs text-muted-foreground">{studentLevel}</span>}
          </div>
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <GraduationCap className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{tutorName}</span>
            {tutorRoleLabel && (
              <span
                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none mt-0.5 self-start"
                style={{
                  background: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
                  color: 'var(--color-2-nuance-1)',
                  border: '1px solid color-mix(in srgb, var(--color-2-nuance-1) 35%, transparent)',
                }}
              >
                {tutorRoleLabel}
              </span>
            )}
          </div>
        </div>
      </td>

      <td>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            <span className="font-medium text-foreground capitalize">{sessionDate}</span>
          </div>
          {sessionTimeRange && (
            <span className="text-xs text-muted-foreground ml-6">{sessionTimeRange}</span>
          )}
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-foreground">{establishmentName}</span>
        </div>
      </td>

      <td>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex">
                <LabelBadge
                  label={subjectCountLabel}
                  icon={BookOpen}
                  variant="ghost"
                  color="var(--color-3-nuance-1)"
                  size="sm"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs">{formatPairSubjectsTooltip(subjects)}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>

      <td>
        <LabelBadge
          label={typeConfig.label}
          icon={TypeIcon}
          variant={typeConfig.badgeVariant}
          color={typeConfig.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          {canView && <Button preset="row-view" onClick={() => onView?.(pair.id)} />}
          {canDelete && <Button preset="row-delete" onClick={() => onDelete?.(pair.id)} />}
        </div>
      </td>
    </TableRow>
  );
};
